import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Save, Calendar, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchProducts } from '../../redux/slices/productSlice';
import { fetchCategories } from '../../redux/slices/categorySlice';
import { getWeeklyShakeMenu, updateWeeklyShakeMenu } from '../../services/weeklyShakeMenuService';
import { CustomDropdown } from '../../components/common/CustomDropdown';

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const WeeklyShakeSetup = () => {
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.product);
  const { categories } = useSelector((state) => state.category);

  const [weeklyMenu, setWeeklyMenu] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load products, categories, and weekly menu on mount
  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchCategories());
    loadWeeklyMenu();
  }, [dispatch]);

  const loadWeeklyMenu = async () => {
    try {
      setLoading(true);
      const menu = await getWeeklyShakeMenu();
      setWeeklyMenu(menu);
    } catch (error) {
      console.error('Error loading weekly menu:', error);
      toast.error('Failed to load weekly menu');
    } finally {
      setLoading(false);
    }
  };

  // Group products by category for dropdown
  const getProductOptions = () => {
    if (!categories || categories.length === 0) {
      return products.map(product => ({
        value: product.id,
        label: product.name || product.productName
      }));
    }

    const options = [];
    categories.forEach(category => {
      const categoryProducts = products.filter(
        p => p.categoryId === category.id
      );

      if (categoryProducts.length > 0) {
        // Add category header as disabled option
        options.push({
          value: `header-${category.id}`,
          label: `━━ ${category.name || category.categoryName} ━━`,
          disabled: true,
          divider: options.length > 0
        });

        // Add products under this category
        categoryProducts.forEach(product => {
          options.push({
            value: product.id,
            label: `  ${product.name || product.productName}`
          });
        });
      }
    });

    return options;
  };

  const handleShakeChange = (day, shakeNumber, productId) => {
    const selectedProduct = products.find(p => p.id === productId);

    if (!selectedProduct) {
      setWeeklyMenu(prev => ({
        ...prev,
        [day]: {
          ...prev[day],
          [shakeNumber]: null
        }
      }));
      return;
    }

    setWeeklyMenu(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [shakeNumber]: {
          id: selectedProduct.id,
          name: selectedProduct.name || selectedProduct.productName,
          categoryId: selectedProduct.categoryId
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateWeeklyShakeMenu(weeklyMenu);
      toast.success('Weekly shake menu saved successfully!');
    } catch (error) {
      console.error('Error saving weekly menu:', error);
      toast.error('Failed to save weekly menu');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadWeeklyMenu();
    toast.success('Reset to saved configuration');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Loading weekly menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 mb-6">
          <div className="p-3 md:p-4 border-b border-slate-100 bg-slate-50">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Configuration
            </h3>
          </div>

          <div className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-lg md:text-2xl font-black text-slate-900 flex items-center gap-2 md:gap-3">
                  <Calendar className="w-6 h-6 md:w-7 md:h-7 text-blue-600" />
                  Weekly Shake Menu Setup
                </h1>
                <p className="text-xs md:text-sm text-slate-500 font-medium mt-1">
                  Configure which 3 shakes are available for each day of the week
                </p>
              </div>

              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-3 w-full md:w-auto">
                <button
                  onClick={handleReset}
                  disabled={saving}
                  className="px-4 py-2.5 bg-slate-200 text-slate-700 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reset
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2.5 bg-green-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span className="hidden sm:inline">Save Configuration</span>
                      <span className="sm:hidden">Save</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Days Configuration */}
        <div className="space-y-3 md:space-y-4">
          {DAYS.map((day, index) => {
            const dayMenu = weeklyMenu[day.key] || { shake1: null, shake2: null, shake3: null };

            return (
              <div
                key={day.key}
                className="bg-white rounded-2xl shadow-sm border border-slate-100"
              >
                <div className="p-3 md:p-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-purple-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base md:text-lg font-black text-slate-900 uppercase tracking-wide">
                      {day.label}
                    </h3>
                    <span className="px-2.5 md:px-3 py-1 bg-white rounded-full text-xs font-black text-slate-600">
                      Day {index + 1}
                    </span>
                  </div>
                </div>

                <div className="p-4 md:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                    {/* Shake 1 */}
                    <div>
                      <label className="block text-xs md:text-sm font-black text-slate-700 mb-2 uppercase tracking-wide">
                        Shake 1
                      </label>
                      <CustomDropdown
                        options={getProductOptions()}
                        value={dayMenu.shake1?.id || ''}
                        onChange={(value) => handleShakeChange(day.key, 'shake1', value)}
                        placeholder="Select first shake"
                        className="w-full"
                      />
                      {dayMenu.shake1 && (
                        <p className="mt-2 text-xs text-green-600 font-medium">
                          ✓ Selected: {dayMenu.shake1.name}
                        </p>
                      )}
                    </div>

                    {/* Shake 2 */}
                    <div>
                      <label className="block text-xs md:text-sm font-black text-slate-700 mb-2 uppercase tracking-wide">
                        Shake 2
                      </label>
                      <CustomDropdown
                        options={getProductOptions()}
                        value={dayMenu.shake2?.id || ''}
                        onChange={(value) => handleShakeChange(day.key, 'shake2', value)}
                        placeholder="Select second shake"
                        className="w-full"
                      />
                      {dayMenu.shake2 && (
                        <p className="mt-2 text-xs text-green-600 font-medium">
                          ✓ Selected: {dayMenu.shake2.name}
                        </p>
                      )}
                    </div>

                    {/* Shake 3 */}
                    <div>
                      <label className="block text-xs md:text-sm font-black text-slate-700 mb-2 uppercase tracking-wide">
                        Shake 3
                      </label>
                      <CustomDropdown
                        options={getProductOptions()}
                        value={dayMenu.shake3?.id || ''}
                        onChange={(value) => handleShakeChange(day.key, 'shake3', value)}
                        placeholder="Select third shake"
                        className="w-full"
                      />
                      {dayMenu.shake3 && (
                        <p className="mt-2 text-xs text-green-600 font-medium">
                          ✓ Selected: {dayMenu.shake3.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Preview */}
                  {(dayMenu.shake1 || dayMenu.shake2 || dayMenu.shake3) && (
                    <div className="mt-3 md:mt-4 p-2.5 md:p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                        Preview
                      </p>
                      <p className="text-xs md:text-sm text-slate-700 font-medium">
                        {day.label}'s Menu: {' '}
                        <span className="text-blue-600 font-bold">
                          {[dayMenu.shake1?.name, dayMenu.shake2?.name, dayMenu.shake3?.name]
                            .filter(Boolean)
                            .join(' & ') || '(Not set)'}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Save Button (Bottom) */}
        <div className="mt-4 md:mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full md:w-auto px-4 md:px-6 py-2.5 md:py-3 bg-green-600 text-white rounded-xl font-black text-xs md:text-sm uppercase tracking-widest hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving Configuration...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">Save Weekly Menu</span>
                <span className="sm:hidden">Save Menu</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeeklyShakeSetup;
